/**
 * Population (§1.3): radiation decay, logistic growth, over-capacity decay, migration with
 * focus bias, fixed neutral pop. Focus-front distances refreshed every 2 s.
 */
import { BALANCE, DT, TICK_HZ } from '../balance.js';
import { axialDist } from '../hex.js';
import type { MatchState } from '../types.js';

const P = BALANCE.pop;
const RAD_DECAY = Math.pow(0.5, 1 / (P.radHalfLifeS * TICK_HZ));

const scratch = new WeakMap<MatchState, Float64Array>();

export function populationSystem(state: MatchState): void {
  const w = state.world;
  const N = w.landCount;
  const { pop, rad, owner, cityMult } = state;
  const { nbr, capBase } = w;

  for (let h = 0; h < N; h++) {
    if (rad[h] > 0) {
      const r = rad[h] * RAD_DECAY;
      rad[h] = r < 1e-3 ? 0 : r;
    }
  }

  if (state.tick % BALANCE.creep.focusDistIntervalTicks === 0) refreshFocusDist(state);

  // effective capacity once per tick (same formula as entities.capEff)
  let ce = scratch.get(state);
  if (!ce) scratch.set(state, (ce = new Float64Array(N)));
  const scale = new Float64Array(state.players.length + 1).fill(1);
  for (const p of state.players) scale[p.id] = p.capScale;
  for (let h = 0; h < N; h++) ce[h] = capBase[h] * cityMult[h] * (1 - P.radCapLoss * rad[h]) * scale[owner[h]];

  // growth / decay / neutral
  for (let h = 0; h < N; h++) {
    const c = ce[h];
    if (owner[h] === 0) {
      pop[h] = P.neutralFrac * c;
      continue;
    }
    const p = pop[h];
    if (p > c) pop[h] = Math.max(c, p - P.overDecay * p * DT);
    else pop[h] = p + P.growth * Math.max(p, P.seedFrac * c) * (1 - p / c) * DT;
  }

  // migration: each same-owner land edge once (i < j)
  const fd = state.focusDist;
  const k0 = (P.migration * DT) / 6;
  for (let i = 0; i < N; i++) {
    const o = owner[i];
    if (!o) continue;
    const ci = ce[i];
    const base = i * 6;
    for (let k = 0; k < 6; k++) {
      const j = nbr[base + k];
      if (j <= i || owner[j] !== o) continue;
      const cj = ce[j];
      let flow = k0 * (pop[i] / ci - pop[j] / cj) * (ci < cj ? ci : cj);
      if (flow === 0) continue;
      const di = fd[i];
      const dj = fd[j];
      if (di !== dj && di !== 255 && dj !== 255) {
        // flow > 0 moves pop i → j
        const towardFront = flow > 0 ? dj < di : di < dj;
        flow *= towardFront ? P.focusToward : P.focusAway;
      }
      pop[i] -= flow;
      pop[j] += flow;
    }
  }
}

/**
 * focusDist[h] = BFS distance (through the owner's land) to the owner's focus front: owned
 * hexes within focusRadius+1 of the focus hex that border non-owned land. 255 = no focus.
 */
export function refreshFocusDist(state: MatchState): void {
  const w = state.world;
  const N = w.landCount;
  const fd = state.focusDist;
  fd.fill(255);
  const queue = new Int32Array(N);
  const R = BALANCE.creep.focusRadius + 1;
  for (const p of state.players) {
    const f = p.focusHex;
    if (f < 0 || p.eliminatedTick >= 0) continue;
    let head = 0;
    let tail = 0;
    for (let h = 0; h < N; h++) {
      if (state.owner[h] !== p.id) continue;
      if (axialDist(w.q[h], w.r[h], w.q[f], w.r[f]) > R) continue;
      let border = false;
      for (let k = 0; k < 6; k++) {
        const n = w.nbr[h * 6 + k];
        if (n >= 0 && state.owner[n] !== p.id) {
          border = true;
          break;
        }
      }
      if (border) {
        fd[h] = 0;
        queue[tail++] = h;
      }
    }
    while (head < tail) {
      const h = queue[head++];
      const d = fd[h] + 1;
      if (d >= 255) continue;
      for (let k = 0; k < 6; k++) {
        const n = w.nbr[h * 6 + k];
        if (n >= 0 && state.owner[n] === p.id && fd[n] === 255) {
          fd[n] = d;
          queue[tail++] = n;
        }
      }
    }
  }
}
